import React from "react";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  imageUrl?: string;
  schemaData?: Record<string, unknown>;
  keywords?: string;
  noIndex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl,
  imageUrl,
  schemaData,
  keywords,
  noIndex = false,
}) => {
  // Base URL for canonical links and images
  const baseUrl = "https://apcg2026-saudiarabia.org"; // Replace with your actual domain

  // Default image if none provided
  const defaultImage = `${baseUrl}/images/logos/apcg-ubt-white-logo.png`;

  // Format the title
  const formattedTitle = title
    ? `${title} | APCG Saudi Arabia`
    : "APCG 2026 | Asia-Pacific Conference on Giftedness Saudi Arabia";

  // Default description if none provided
  const defaultDescription =
    "The Asia-Pacific Conference on Giftedness (APCG) 2026, hosted in Jeddah, Saudi Arabia, from February 7-11, 2026 is a transformative gathering of global educators, researchers, and practitioners.";

  return (
    <Helmet>
      {/* Basic metadata */}
      <title>{formattedTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical URL */}
      {canonicalUrl && (
        <link rel="canonical" href={`${baseUrl}${canonicalUrl}`} />
      )}

      {/* Robots directives */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta
        property="og:url"
        content={canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl}
      />
      <meta property="og:title" content={formattedTitle} />
      <meta
        property="og:description"
        content={description || defaultDescription}
      />
      <meta property="og:image" content={imageUrl || defaultImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:url"
        content={canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl}
      />
      <meta name="twitter:title" content={formattedTitle} />
      <meta
        name="twitter:description"
        content={description || defaultDescription}
      />
      <meta name="twitter:image" content={imageUrl || defaultImage} />

      {/* JSON-LD structured data */}
      {schemaData && (
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
